/**
 * Helper functions for chart components
 */

/**
 * Get maximum Y-axis value based on answer type
 *
 * @param {string} answerType - The type of answer
 * @return {number|null} The maximum Y-axis value
 */
export function getMaxYAxisForAnswerType(answerType) {
    switch (answerType) {
        case 'Cijfer':
            return 10;
        case 'Jaren' || 'Uren' || 'Aantal' || 'Dagen':
            return null;
        case 'Aantal':
            return 100;
        default:
            return 100;
    }
}

/**
 * Format chart tooltip label with appropriate units based on answer type
 *
 * @param {Object} context - The chart.js tooltip context
 * @param {string} answerType - The type of answer
 * @return {string} Formatted label with units
 */
export function formatTooltipLabel(context, answerType) {
    let label = context.dataset.label || '';
    if (label) {
        label += ': ';
    }

    let value = null;
    if (context.chart.options.indexAxis === 'y') {
        value = context.parsed.x;
    } else {
        value = context.parsed.y;
    }

    if (value !== null) {
        label += value;
        if (answerType === 'Dagen') {
            label += ' dagen';
        } else if (answerType === 'Jaren') {
            label += ' jaren';
        } else if (answerType === 'Aantal') {
            label += ' stuks';
        } else if (answerType === 'Uren') {
            label += ' uren';
        } else {
            label += '%';
        }
    }
    return label;
}

/**
 * Escapes a value for CSV format
 *
 * @param {*} value - The value to escape
 * @return {string} The escaped value
 */
function escapeCSV(value) {
    if (value === null || value === undefined) {
        return '';
    }

    const stringValue = String(value);

    if (
        stringValue.includes('"') ||
        stringValue.includes(',') ||
        stringValue.includes('\n') ||
        stringValue.includes('\r')
    ) {
        return '"' + stringValue.replace(/"/g, '""') + '"';
    }

    return stringValue;
}

/**
 * Creates and downloads a CSV file from chart data
 *
 * @param {Object} options - Configuration options
 * @param {Object} options.chart - The chart instance with config.data containing labels and datasets
 * @param {Node} options.shadowRoot - The component's shadow root for creating and appending download link
 * @param {string} [options.filename='Chart'] - Filename for the downloaded CSV
 * @return {void}
 */
export function downloadChartCSV({ chart, shadowRoot, filename = 'Chart' }) {
    const headerRow = ['Category', ...chart.config.data.labels].map(escapeCSV).join(',');

    const dataRows = chart.config.data.datasets.map(dataset => {
        const rowValues = [dataset.label, ...dataset.data].map(escapeCSV);
        return rowValues.join(',');
    });

    const csvContent = 'data:text/csv;charset=utf-8,' + encodeURIComponent(headerRow + '\r\n' + dataRows.join('\r\n'));

    const downloadLink = document.createElement('a');
    downloadLink.setAttribute('href', csvContent);
    downloadLink.setAttribute('download', `${filename}.csv`);
    shadowRoot.appendChild(downloadLink);
    downloadLink.click();
    downloadLink.remove();
}
